import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { ArrowUpRight, CalendarDays, Clock, Video, ShieldCheck } from "lucide-react";

const FALLBACK_SLOTS = [
  "09:00", "09:30", "10:00", "10:30",
  "11:00", "13:00", "13:30", "14:00",
  "14:30", "15:00", "15:30", "16:00", "16:30",
];

export default function BookPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [slotIso, setSlotIso] = useState("");
  const [slots, setSlots] = useState({ slots: [], unconstrained: true, host_timezone: "UTC" });
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", company_address: "",
    role: "Founder", capital_range: "$30M – $50M",
    authorized_signatory: false,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const clientTz = form.timezone;

  const setField = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: typeof e === "string" ? e : e.target.value }));

  const toYmd = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };
  const fmtSlot = (iso, tz) => new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: tz });

  useEffect(() => {
    if (!date) { setSlots({ slots: [], unconstrained: true, host_timezone: "UTC" }); return; }
    let active = true;
    setSlotsLoading(true); setSlotIso(""); setTime("");
    api.get("/bookings/slots", { params: { date: toYmd(date) } })
      .then((r) => { if (active) setSlots(r.data); })
      .catch(() => { if (active) setSlots({ slots: [], unconstrained: true, host_timezone: "UTC" }); })
      .finally(() => { if (active) setSlotsLoading(false); });
    return () => { active = false; };
  }, [date]);

  const timeChosen = slots.unconstrained ? !!time : !!slotIso;

  const submit = async () => {
    if (!date || !timeChosen) { toast.error("Pick a date and time"); return; }
    let preferred_date = toYmd(date);
    let preferred_time = time;
    if (!slots.unconstrained) {
      const d = new Date(slotIso);
      const p = (n) => String(n).padStart(2, "0");
      preferred_date = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
      preferred_time = `${p(d.getHours())}:${p(d.getMinutes())}`;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        preferred_date,
        preferred_time,
      });
      setConfirmation(data);
      setStep(4);
      toast.success("Discovery call booked");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#050505] text-white min-h-screen grain">
      <NavBar />
      <section className="pt-32 pb-20 hero-glow">
        <div className="mx-auto max-w-6xl px-6 lg:px-12">
          <div className="label-kicker">Discovery Call</div>
          <h1 className="font-display text-5xl lg:text-6xl font-light tracking-tight mt-4 leading-[1.04]">
            30 minutes to map your path to capital.
          </h1>
          <p className="text-white/60 mt-6 max-w-2xl">
            A confidential, NDA-governed conversation. You leave with a clear diagnosis
            across entity, operations, narrative, and lender fit.
          </p>

          {/* Stepper */}
          <div className="flex items-center gap-6 mt-12 text-xs">
            {["When", "About You", "Sign NDA", "Confirmed"].map((label, i) => {
              const n = i + 1;
              const active = n === step;
              const done = n < step;
              return (
                <div key={label} className="flex items-center gap-3">
                  <div className={`w-7 h-7 border ${active || done ? "border-white text-white" : "border-white/20 text-white/40"} flex items-center justify-center font-mono`}>
                    {n}
                  </div>
                  <span className={`label-kicker ${active ? "text-white" : ""}`}>{label}</span>
                  {i < 3 && <div className="w-12 h-px bg-white/10" />}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Reveal as="section" className="pb-32">
        <div className="mx-auto max-w-6xl px-6 lg:px-12 grid lg:grid-cols-12 gap-px bg-white/10">
          {/* Left rail trust */}
          <aside className="lg:col-span-4 bg-[#050505] p-8 space-y-6">
            <div>
              <div className="label-kicker flex items-center gap-2"><CalendarDays className="h-3 w-3" /> Duration</div>
              <div className="text-white mt-2">30 minutes</div>
            </div>
            <div>
              <div className="label-kicker flex items-center gap-2"><Video className="h-3 w-3" /> Platform</div>
              <div className="text-white mt-2">Jitsi Meet (browser, no install)</div>
            </div>
            <div>
              <div className="label-kicker flex items-center gap-2"><ShieldCheck className="h-3 w-3" /> Confidentiality</div>
              <div className="text-white mt-2">NDA-governed, encrypted</div>
            </div>
          </aside>

          {/* Right panel */}
          <div className="lg:col-span-8 bg-[#050505] p-8 lg:p-12">
            {step === 1 && (
              <div className="grid md:grid-cols-2 gap-10">
                <div>
                  <div className="label-kicker">Pick a date</div>
                  <div className="mt-4 inline-block border border-white/10">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => setDate(d)}
                      disabled={(d) => {
                        const today = new Date(); today.setHours(0,0,0,0);
                        return d < today || d.getDay() === 0 || d.getDay() === 6;
                      }}
                      data-testid="booking-calendar"
                      className="bg-transparent text-white"
                    />
                  </div>
                </div>
                <div>
                  <div className="label-kicker">Pick a time</div>
                  {!date ? (
                    <p className="text-xs text-white/40 mt-4" data-testid="bk-pick-date-hint">Choose a date first.</p>
                  ) : slotsLoading ? (
                    <p className="text-xs text-white/40 mt-4">Checking availability…</p>
                  ) : slots.unconstrained ? (
                    <div className="grid grid-cols-3 gap-2 mt-4">
                      {FALLBACK_SLOTS.map((t) => (
                        <button
                          key={t}
                          data-testid={`time-${t}`}
                          onClick={() => setTime(t)}
                          className={`px-3 py-2 text-sm border ${time === t ? "border-white bg-white text-black" : "border-white/15 text-white/80 hover:border-white/40"} transition-colors`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  ) : slots.slots.length === 0 ? (
                    <p className="text-xs text-amber-300/80 mt-4" data-testid="bk-no-slots">No open times on this day. Please try another date.</p>
                  ) : (
                    <>
                      <p className="text-[11px] text-white/40 mt-3" data-testid="bk-tz-note">
                        Times in your timezone (<span className="text-white/60">{clientTz}</span>)
                        {slots.host_timezone && slots.host_timezone !== clientTz ? <> · host in {slots.host_timezone}</> : null}
                      </p>
                      <div className="grid grid-cols-3 gap-2 mt-3" data-testid="bk-slots">
                        {slots.slots.map((iso) => (
                          <button
                            key={iso}
                            data-testid={`slot-${iso}`}
                            onClick={() => setSlotIso(iso)}
                            className={`px-3 py-2 text-xs border text-center leading-tight ${slotIso === iso ? "border-white bg-white text-black" : "border-white/15 text-white/80 hover:border-white/40"} transition-colors`}
                          >
                            {fmtSlot(iso, clientTz)}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  <div className="label-kicker mt-8 flex items-center gap-2"><Clock className="h-3 w-3" /> Timezone</div>
                  <div className="text-sm text-white/60 mt-2">{form.timezone}</div>

                  <Button
                    data-testid="booking-next"
                    disabled={!date || !timeChosen}
                    onClick={() => setStep(2)}
                    className="rounded-none bg-white text-black hover:bg-white/90 mt-10 w-full h-12 btn-shimmer"
                  >
                    Continue <ArrowUpRight className="ml-2 h-4 w-4" strokeWidth={1.25} />
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Full name">
                    <Input data-testid="bk-name" value={form.name} onChange={setField("name")} placeholder="Jane Doe" className="rounded-none bg-black/40 border-white/15" />
                  </Field>
                  <Field label="Email">
                    <Input data-testid="bk-email" type="email" value={form.email} onChange={setField("email")} placeholder="jane@firm.com" className="rounded-none bg-black/40 border-white/15" />
                  </Field>
                  <Field label="Phone">
                    <Input data-testid="bk-phone" value={form.phone} onChange={setField("phone")} placeholder="+1 555 123 4567" className="rounded-none bg-black/40 border-white/15" />
                  </Field>
                  <Field label="Company">
                    <Input data-testid="bk-company" value={form.company} onChange={setField("company")} placeholder="Firm name" className="rounded-none bg-black/40 border-white/15" />
                  </Field>
                  <Field label="Company address" className="md:col-span-2">
                    <Input data-testid="bk-company-address" value={form.company_address} onChange={setField("company_address")} placeholder="123 Main St, Suite 400, City, State ZIP" className="rounded-none bg-black/40 border-white/15" />
                  </Field>
                  <Field label="Role">
                    <Select value={form.role} onValueChange={setField("role")}>
                      <SelectTrigger data-testid="bk-role" className="rounded-none bg-black/40 border-white/15"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Founder", "CEO / Principal", "CFO", "Family Office", "Fund / Lender", "Advisor", "Other"].map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Capital range">
                    <Select value={form.capital_range} onValueChange={setField("capital_range")}>
                      <SelectTrigger data-testid="bk-capital" className="rounded-none bg-black/40 border-white/15"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["$30M – $50M", "$50M – $100M", "$100M – $250M", "$250M+"].map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="What are you trying to close? (optional)">
                  <Textarea data-testid="bk-notes" value={form.notes} onChange={setField("notes")} rows={4}
                    placeholder="A brief description of the deal, the target lender type, and the timing."
                    className="rounded-none bg-black/40 border-white/15" />
                </Field>
                <label data-testid="bk-signatory-label" className="flex items-start gap-3 cursor-pointer select-none border border-white/10 bg-black/20 p-4">
                  <input data-testid="bk-signatory" type="checkbox" checked={form.authorized_signatory}
                    onChange={(e) => setForm((s) => ({ ...s, authorized_signatory: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--gold))]" />
                  <span className="text-xs text-white/60 leading-relaxed">
                    I am the Authorized Signatory for the Company listed above.
                  </span>
                </label>
                <div className="flex items-center gap-3 pt-2">
                  <Button data-testid="bk-back" variant="outline" className="rounded-none border-white/20 hover:bg-white/10" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button
                    data-testid="bk-to-nda"
                    onClick={() => setStep(3)}
                    disabled={!form.name || !form.email || !form.phone || !form.company || !form.company_address || !form.role || !form.capital_range || !form.authorized_signatory}
                    className="rounded-none bg-white text-black hover:bg-white/90 px-7 h-11 btn-shimmer"
                  >
                    Continue to NDA <ArrowUpRight className="ml-2 h-4 w-4" strokeWidth={1.25} />
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6" data-testid="bk-nda-step">
                <div>
                  <div className="label-kicker">Sign the Mutual NDA</div>
                  <p className="text-white/60 text-sm mt-2 max-w-xl">
                    Our conversation is confidential. Please review and sign the Mutual
                    Non-Disclosure, Non-Circumvention and Commercial Covenant Agreement below,
                    then confirm your discovery call.
                  </p>
                </div>
                <div className="border border-white/10 bg-white" style={{ height: 620 }}>
                  <iframe
                    title="D.O.X Mutual NDA"
                    data-testid="bk-nda-iframe"
                    src="https://doxcapital.na4.documents.adobe.com/public/esignWidget?wid=CBFCIBAA3AAABLblqZhAzy-0FG0JeY1UL50B-5T9hvNZ1i4SrQPfZQp8tfiA-ZT66yuVIc5VjIwrB4OakL3U*&hosted=false"
                    width="100%" height="100%" frameBorder="0"
                    style={{ border: 0, overflow: "hidden" }}
                  />
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <Button data-testid="bk-nda-back" variant="outline" className="rounded-none border-white/20 hover:bg-white/10" onClick={() => setStep(2)}>
                    Back
                  </Button>
                  <Button
                    data-testid="bk-submit"
                    onClick={submit}
                    disabled={submitting || !date || !time}
                    className="rounded-none bg-white text-black hover:bg-white/90 px-7 h-11 btn-shimmer"
                  >
                    {submitting ? "Booking…" : "I've signed, Confirm Discovery Call"}
                  </Button>
                </div>
              </div>
            )}

            {step === 4 && confirmation && (
              <div data-testid="bk-confirmation" className="py-8">
                <div className="label-kicker">Confirmed</div>
                <h2 className="font-display text-4xl mt-3">You're on the calendar.</h2>
                <p className="text-white/65 mt-4 max-w-lg">
                  An invitation has been sent to <span className="text-white">{form.email}</span>.
                  Bring the most pressing version of your file, even if it's incomplete.
                </p>
                <div className="mt-8 border border-white/15 p-6 grid sm:grid-cols-2 gap-6 max-w-2xl">
                  <Detail label="Date">{date.toDateString()}</Detail>
                  <Detail label="Time">{slots.unconstrained ? `${time} ${form.timezone}` : (slotIso ? `${fmtSlot(slotIso, clientTz)} ${clientTz}` : "")}</Detail>
                  <Detail label="Platform">Jitsi Meet</Detail>
                  <Detail label="Meeting link">
                    <a href={confirmation.meet_link} target="_blank" rel="noreferrer"
                       className="text-[hsl(var(--gold))] hover:underline break-all" data-testid="bk-meet-link">
                      {confirmation.meet_link}
                    </a>
                  </Detail>
                </div>
                <div className="flex gap-3 mt-10">
                  <Button onClick={() => nav("/")} variant="outline" className="rounded-none border-white/20 hover:bg-white/10">Back to home</Button>
                  <Button onClick={() => nav("/auth")} className="rounded-none bg-white text-black hover:bg-white/90">Create Portal Account</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Reveal>

      <Footer />
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="label-kicker">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
function Detail({ label, children }) {
  return (
    <div>
      <div className="label-kicker">{label}</div>
      <div className="text-white text-sm mt-1">{children}</div>
    </div>
  );
}
